import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { SelectionContext, type SelectionApi } from '../lib/selection-context';

type Props = {
  children: ReactNode;
};

/**
 * Provides multi-select state for the Library AssetGrid. State lives only in
 * memory — selection clears on filter-change (caller's responsibility via
 * `clear()` in a useEffect on filter-deps), on route-change (Provider unmount),
 * on Escape key (handled here, dialog-aware skip), and explicitly via the
 * action-bar "Auswahl aufheben" button.
 *
 * Pattern reference (patterns/react.md): Window-level Esc handler must skip
 * when `[role="dialog"]` or `[role="alertdialog"]` is open — otherwise the
 * shortcut hijacks dialog dismissal.
 */
export function SelectionProvider({ children }: Props) {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  // Anchor for shift-click range selection — last id that was toggled or
  // explicitly selected. Resets to null on clear().
  const anchorRef = useRef<string | null>(null);

  const has = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchorRef.current = id;
  }, []);

  const selectRange = useCallback((id: string, allIds: readonly string[]) => {
    const anchor = anchorRef.current;
    if (!anchor || !allIds.includes(anchor)) {
      // Graceful fallback: no anchor or anchor lost (e.g. asset filtered out)
      // → behave like a plain toggle.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      anchorRef.current = id;
      return;
    }
    const a = allIds.indexOf(anchor);
    const b = allIds.indexOf(id);
    if (a === -1 || b === -1) return;
    const [from, to] = a <= b ? [a, b] : [b, a];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (let i = from; i <= to; i++) next.add(allIds[i]!);
      return next;
    });
    // Anchor stays at the original anchor — repeated shift-click expands
    // from the same starting point (standard list-select behavior).
  }, []);

  const setMany = useCallback((ids: readonly string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) for (const id of ids) next.add(id);
      else for (const id of ids) next.delete(id);
      return next;
    });
    // Anchor an das letzte Element setzen (deselect-all → null), damit ein
    // folgender Shift-Klick eine sinnvolle Range hat.
    anchorRef.current = selected && ids.length > 0 ? ids[ids.length - 1]! : null;
  }, []);

  const clear = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
    anchorRef.current = null;
  }, []);

  // ESC-clears-selection. Dialog-aware: if any [role="dialog"] or
  // [role="alertdialog"] is open, let the dialog's own handler dismiss it
  // instead — selection stays. Also skip when an editable element is focused
  // (analogous to AssetDetailPage's pattern, prevents Esc-during-tag-edit
  // from clearing selection unexpectedly).
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      if (selectedIds.size === 0) return; // nothing to clear
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }
      if (document.querySelector('[role="alertdialog"], [role="dialog"]')) return;
      e.preventDefault();
      clear();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIds.size, clear]);

  const api = useMemo<SelectionApi>(
    () => ({ selectedIds, count: selectedIds.size, has, toggle, selectRange, setMany, clear }),
    [selectedIds, has, toggle, selectRange, setMany, clear],
  );

  return <SelectionContext.Provider value={api}>{children}</SelectionContext.Provider>;
}
